import { ImageIcon, Download, Zap, IndianRupee, ArrowRight, CheckCircle2 } from "lucide-react";
import { FeaturePageLayout } from "../FeaturePageLayout";
import { PageMeta } from "@/components/seo/PageMeta";
import { useAuthDialog } from "@/lib/auth-dialog-context";

const BENEFITS = [
  {
    icon: Zap,
    title: "Batch compress up to 10 images",
    desc: "Upload multiple JPG, PNG, or WEBP files at once. Each compresses simultaneously — no waiting for one to finish before the next.",
  },
  {
    icon: ImageIcon,
    title: "Adjustable quality slider",
    desc: "Choose compression level from 10% to 95%. See original vs. compressed file size per image and the overall savings percentage.",
  },
  {
    icon: Download,
    title: "Download individually or as ZIP",
    desc: "Download each compressed image separately or grab all of them in a single ZIP file.",
  },
  {
    icon: IndianRupee,
    title: "Lower your shipping weight slab",
    desc: "Meesho's shipping cost is based on package weight — which includes product images on the label. Smaller image files mean smaller printed labels and a lower weight slab.",
  },
];

export const PriceReducerLandingPage = () => {
  const { openAuth } = useAuthDialog();

  return (
    <FeaturePageLayout currentPath="/price-reducer">
      <PageMeta
        title="Price Reducer — Free Bulk Image Compressor for Product Photos"
        description="Compress up to 10 product images at once, cut file size without losing quality, and lower your Meesho shipping weight slab."
        keywords="meesho image compressor, compress product photos meesho, reduce image size meesho, lower shipping cost meesho, bulk image compress india, product photo compressor"
        path="/price-reducer"
        ogTitle="Price Reducer — Bulk Image Compressor for Meesho Sellers | EasyLister"
        ogDescription="Compress up to 10 product photos at once, reduce file size, and lower your Meesho shipping weight slab. Free, in-browser."
        twitterTitle="Cut product image size & lower Meesho shipping costs"
        twitterDescription="Batch compress up to 10 product photos. Smaller images = lower shipping slab. Free, all in-browser."
      />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-6">
              EasyLister · Price Reducer
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Free Bulk Image Compressor{" "}
              <span className="bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">
                for Product Photos
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Compress up to 10 product images at once without losing visible quality. Smaller image
              files lower your Meesho shipping weight slab, saving you ₹15–₹35 per order. All
              processing runs in your browser — nothing is uploaded anywhere.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => openAuth("signup")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all text-sm"
              >
                Try Free — No Card Needed <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Savings callout */}
      <section className="py-10 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { val: "10 images", label: "Per batch" },
              { val: "₹15–₹35", label: "Saved per order" },
              { val: "10–95%", label: "Quality range" },
              { val: "ZIP", label: "Batch download" },
            ].map(({ val, label }) => (
              <div key={label}>
                <div className="font-heading text-2xl font-extrabold text-white">{val}</div>
                <div className="text-emerald-100 text-xs mt-1 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-12">
            Why sellers use Price Reducer
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-6 rounded-2xl border border-slate-100 bg-slate-50 hover:border-purple-200 hover:bg-purple-50/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gradient-to-b from-white to-purple-50/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-10">
            Three steps to smaller files
          </h2>
          <ol className="space-y-6">
            {[
              "Upload up to 10 product images — JPG, PNG, or WEBP",
              "Set your quality level using the slider (we recommend 70–80% for best balance)",
              "Download each image individually or grab them all in a ZIP",
            ].map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-extrabold flex items-center justify-center">
                  {i + 1}
                </span>
                <p className="text-slate-700 text-base leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </FeaturePageLayout>
  );
};
